/**
 * Deep Links Handler for Capacitor App
 *
 * Handles Universal Links (iOS) and App Links (Android) for:
 * - Email confirmation: parentpath.de/auth/confirm?token=...
 * - Partner invitations: parentpath.de/invite/{token}
 * - OAuth callbacks: parentpath.de/auth/callback
 */

import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabase';

/** Module-level flag to ensure getLaunchUrl() is only processed once per app lifecycle */
let launchUrlProcessed = false;

export interface DeepLinkResult {
  type: 'auth_confirm' | 'invite' | 'oauth_callback' | 'generic' | 'error';
  path: string;
  params: Record<string, string>;
  error?: string;
}

/**
 * Parse a deep link URL and determine its type and parameters
 */
export function parseDeepLink(url: string): DeepLinkResult {
  try {
    const urlObj = new URL(url);

    // For custom-scheme URLs the URL parser puts the first path segment into
    // `host` (e.g. `parentpath://auth/callback` → host=`auth`, pathname=`/callback`).
    // Reconstruct the logical pathname so downstream prefix checks behave the
    // same as for the equivalent https URL.
    let pathname = urlObj.pathname;
    if (urlObj.protocol === 'parentpath:' && urlObj.host) {
      const suffix = urlObj.pathname === '/' ? '' : urlObj.pathname;
      pathname = `/${urlObj.host}${suffix}`;
    }

    // Extract query parameters
    const params: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    // Also check hash fragment (Supabase often uses this)
    const hashParams = new URLSearchParams(urlObj.hash.substring(1));
    hashParams.forEach((value, key) => {
      params[key] = value;
    });

    // Email confirmation: /auth/confirm?token=...&type=...
    if (pathname.startsWith('/auth/confirm')) {
      return {
        type: 'auth_confirm',
        path: pathname,
        params,
      };
    }

    // Partner invitation: /invite/{token}
    if (pathname.startsWith('/invite/')) {
      const token = pathname.split('/invite/')[1];
      return {
        type: 'invite',
        path: pathname,
        params: { ...params, token },
      };
    }

    // OAuth callback: /auth/callback
    if (pathname.startsWith('/auth/callback')) {
      return {
        type: 'oauth_callback',
        path: pathname,
        params,
      };
    }

    // Generic deep link - navigate to the path
    return {
      type: 'generic',
      path: pathname,
      params,
    };
  } catch (error) {
    console.error('Failed to parse deep link URL:', url, error);
    return {
      type: 'error',
      path: '/',
      params: {},
      error: 'Invalid URL format',
    };
  }
}

/**
 * Handle email confirmation deep links
 * Verifies the token with Supabase and returns the result
 */
export async function handleAuthConfirm(
  params: Record<string, string>
): Promise<{
  success: boolean;
  error?: string;
  redirectTo: string;
}> {
  const { token_hash, type } = params;

  // If we have access_token and refresh_token (from hash fragment)
  if (params.access_token && params.refresh_token) {
    try {
      const { error } = await supabase.auth.setSession({
        access_token: params.access_token,
        refresh_token: params.refresh_token,
      });

      if (error) {
        return {
          success: false,
          error: error.message,
          redirectTo: '/login?error=session_failed',
        };
      }

      // Determine redirect based on auth type
      if (type === 'recovery') {
        return { success: true, redirectTo: '/reset-password/confirm' };
      }

      // Session is active after setSession — go directly to home
      return { success: true, redirectTo: '/' };
    } catch (err) {
      console.error('Error setting session:', err);
      return {
        success: false,
        error: 'Failed to set session',
        redirectTo: '/login?error=auth_failed',
      };
    }
  }

  // If we have token_hash (OTP verification)
  if (token_hash && type) {
    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash,
        type: type as 'signup' | 'recovery' | 'email',
      });

      if (error) {
        return {
          success: false,
          error: error.message,
          redirectTo: '/login?error=verification_failed',
        };
      }

      if (type === 'recovery') {
        return { success: true, redirectTo: '/reset-password/confirm' };
      }

      // Session is active after verifyOtp — go directly to home
      return { success: true, redirectTo: '/' };
    } catch (err) {
      console.error('Error verifying OTP:', err);
      return {
        success: false,
        error: 'Failed to verify token',
        redirectTo: '/login?error=auth_failed',
      };
    }
  }

  return {
    success: false,
    error: 'No valid auth parameters found',
    redirectTo: '/login?error=invalid_link',
  };
}

/**
 * Handle partner invitation deep links
 * Stores the token for processing after login
 */
export function handleInvite(params: Record<string, string>): {
  token: string;
  redirectTo: string;
} {
  const { token } = params;

  if (!token) {
    console.error('No invite token found in deep link');
    return { token: '', redirectTo: '/login?error=invalid_invite' };
  }

  // Store the invite token for processing after login
  localStorage.setItem('pendingInviteToken', token);

  return {
    token,
    redirectTo: `/invite/${token}`,
  };
}

/**
 * Initialize deep link listener for the Capacitor app
 * Returns a cleanup function to remove listeners
 */
export function initDeepLinkListener(
  onDeepLink: (result: DeepLinkResult) => void
): () => void {
  if (!Capacitor.isNativePlatform()) {
    // Web URL handling is done by useDeepLinks hook with a ref guard
    return () => {};
  }

  // Handle app opened via deep link while running
  const handleAppUrlOpen = (event: URLOpenListenerEvent) => {
    console.log('Deep link received:', event.url);
    const result = parseDeepLink(event.url);
    onDeepLink(result);
  };

  const listenerHandle = App.addListener('appUrlOpen', handleAppUrlOpen);

  // Check if app was opened via deep link (cold start)
  // Only process once per app lifecycle to prevent infinite loops
  if (!launchUrlProcessed) {
    launchUrlProcessed = true;
    App.getLaunchUrl().then((launchUrl) => {
      if (launchUrl?.url) {
        console.log('App launched with URL:', launchUrl.url);
        const result = parseDeepLink(launchUrl.url);
        onDeepLink(result);
      }
    });
  }

  // Return cleanup function — only remove the appUrlOpen listener, not all App listeners
  return () => {
    listenerHandle.then((handle) => handle.remove());
  };
}

/**
 * Get the appropriate deep link URL for sharing
 * Uses the production domain for consistency
 */
export function getDeepLinkUrl(path: string): string {
  const baseUrl = 'https://app.parentpath.de';
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
}

/**
 * Generate an invite deep link URL
 */
export function getInviteDeepLink(token: string): string {
  return getDeepLinkUrl(`/invite/${token}`);
}
